import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import React from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaConfigurationDto } from '@cineamo/api/models/cinema-configuration/CinemaConfigurationDto.types';

import { getCinemaById } from '@cineamo/api/resources/cinema/cinema-api';

import { getCountryFromCode } from '@cineamo/ui/src/helpers/international-helper';

import { IconCircleCrossed16 } from '../icons/ic-circleCrossed';
import { IconStarSolid16 } from '../icons/ic-starSolid';

import { ISO_3166_2_LOCALE } from './locale-helper';
import { mapLinkDefault, mapLinkMobile } from './location-helper';

export const CINEMA_ID_PLACEHOLDER = 'name-of-cinema';

export function cinemaIdOrSlug(cinema?: CinemaDto): number | string {
    return cinema?.slug || cinema?.id || CINEMA_ID_PLACEHOLDER;
}

export function getISO_3166_2(cinema?: CinemaDto): ISO_3166_2_LOCALE | null {
    if (!cinema || !cinema.countryCode || !cinema.stateAbbr) {
        return null;
    }
    return `${cinema.countryCode}-${cinema.stateAbbr}` as ISO_3166_2_LOCALE;
}

export function checkForCinemaPlaceholderOrUndefined(cinemaIdOrSlug: number | string): boolean {
    return !cinemaIdOrSlug || cinemaIdOrSlug === CINEMA_ID_PLACEHOLDER;
}

export function constructCinemaAddress(cinema: CinemaDto): string {
    return `${cinema.street && cinema.street}${cinema.postalCode && `, ${cinema.postalCode}`}
        ${cinema.city && ` ${cinema.city}`}`;
}

export function getCinemaInitials(cinema: CinemaDto): string {
    const cinemaName = cinema?.name || '';
    const initials: string[] = [];
    if (cinemaName.length > 2) {
        let textParts: string[] = cinemaName.split(' ');
        if (textParts.length <= 1) {
            textParts = cinemaName.split('-');
        }
        if (textParts.length >= 1) {
            const firstInitial = textParts[0]?.[0];
            if (firstInitial) {
                initials.push(firstInitial);
            }
            if (textParts.length >= 2) {
                const secondInitial = textParts[1]?.[0];
                if (secondInitial) {
                    initials.push(secondInitial);
                }
            }
        }
    } else if (cinemaName.length > 0) {
        if (cinemaName[0]) {
            initials.push(cinemaName[0]);
        }
        if (cinemaName[1]) {
            initials.push(cinemaName[1]);
        }
    }
    return initials.join('').toUpperCase();
}

export function getCinemaLogo(cinema: CinemaDto, useProfileImageAsFallback = false): string | undefined {
    const logo = cinema?.logoImageUrl;
    if (!logo && useProfileImageAsFallback) {
        return cinema?.profileImageUrl;
    }
    return logo;
}

export function jsxMapLinkMobile(options: { cinema: CinemaDto; locale: string }) {
    const cinema = options.cinema;
    const fullAddressParts: string[] = [];
    const postalCodeCityParts: string[] = [];
    if (cinema.street) {
        fullAddressParts.push(cinema.street);
    }
    if (cinema.postalCode) {
        postalCodeCityParts.push(cinema.postalCode);
    }
    if (cinema.city) {
        postalCodeCityParts.push(cinema.city);
    }
    fullAddressParts.push(postalCodeCityParts.join(' '));
    if (cinema.countryCode) {
        fullAddressParts.push(cinema.countryCode);
    }
    const fullAddress = fullAddressParts.join(', ');
    return (
        <a
            className="flex flex-col"
            href={mapLinkMobile(cinema.street, cinema.postalCode, cinema.city)}>
            <div className="flex flex-row">{fullAddress && <span>{fullAddress}</span>}</div>
        </a>
    );
}

export function jsxMapLinkDefault(options: { cinema: CinemaDto; locale: string }) {
    const cinema = options.cinema;
    const locale = options.locale;
    const fullAddressParts: string[] = [];
    const postalCodeCityParts: string[] = [];
    if (cinema.street) {
        fullAddressParts.push(cinema.street);
    }
    if (cinema.postalCode) {
        postalCodeCityParts.push(cinema.postalCode);
    }
    if (cinema.city) {
        postalCodeCityParts.push(cinema.city);
    }
    fullAddressParts.push(postalCodeCityParts.join(' '));

    const country = cinema.countryCode ? getCountryFromCode(cinema.countryCode, locale) : null;
    if (country) {
        fullAddressParts.push(country);
    }
    const fullAddress = fullAddressParts.join(', ');
    return (
        <a
            className="flex flex-col"
            href={mapLinkDefault(cinema.street, cinema.postalCode, cinema.city)}>
            <div className="flex flex-row">{fullAddress && <span>{fullAddress}</span>}</div>
        </a>
    );
}

export function getDeterministicColorFromString(
    text: string,
    options?: {
        minLuminance: number;
    }
): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = char + ((hash << 5) - hash);
    }

    let r = hash & 0xff;
    let g = (hash >> 8) & 0xff;
    let b = (hash >> 16) & 0xff;

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

    const minLuminance = options?.minLuminance;
    if (luminance < minLuminance) {
        const factor = minLuminance / luminance;
        r = Math.min(255, r * factor);
        g = Math.min(255, g * factor);
        b = Math.min(255, b * factor);
    }

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

type CinemaRequestSettings = {
    status: string;
    icon: React.JSX.Element;
    color: string;
};

type CinemaStatusProps = {
    cinema: CinemaDto;
};

function getCinemaRequestSettings(cinema: CinemaDto): CinemaRequestSettings {
    const config = cinema._embedded?.cinemaConfiguration;
    if (!config) {
        return null;
    }
    const isPrivateBookingEnabled = config.isPrivateBookingEnabled == true;
    const isProgramSuggestionEnabled = config.isProgramSuggestionEnabled == true;
    let cinemaRequestSettings: CinemaRequestSettings = null;
    if (!isPrivateBookingEnabled && !isProgramSuggestionEnabled) {
        cinemaRequestSettings = {
            status: 'cinema.status.bookings-temporarily-unavailable',
            icon: <IconCircleCrossed16 />,
            color: 'text-red'
        };
    } else if (isPrivateBookingEnabled && isProgramSuggestionEnabled) {
        cinemaRequestSettings = {
            status: 'cinema.status.premium-partner',
            icon: <IconStarSolid16 />,
            color: 'text-cyan-dark'
        };
    }
    return cinemaRequestSettings;
}

export function CinemaRequestStatus(props: CinemaStatusProps): React.JSX.Element {
    const t = useTranslations();
    const cinemaRequestSettings = getCinemaRequestSettings(props.cinema);

    if (!cinemaRequestSettings) {
        return null;
    }

    return (
        <div className={classNames('flex flex-row items-center space-x-4', cinemaRequestSettings.color)}>
            {cinemaRequestSettings.icon}
            <span className="text-body-bold-10">{t(cinemaRequestSettings.status)}</span>
        </div>
    );
}

export function isCinemaRequestDisabled(cinemaConfiguration: CinemaConfigurationDto): boolean | null {
    const config = cinemaConfiguration;
    if (!config) {
        return null;
    }
    const isPrivateBookingEnabled = config.isPrivateBookingEnabled == true;
    const isProgramSuggestionEnabled = config.isProgramSuggestionEnabled == true;
    return !isPrivateBookingEnabled && !isProgramSuggestionEnabled;
}

export async function getCinemasByIds(ids: number[]): Promise<CinemaDto[]> {
    const requests = ids.map((id) => getCinemaById(id));
    return await Promise.all(requests);
}
